import React from "react";
import { Button, Link, makeStyles, Spinner, typographyStyles } from "@fluentui/react-components";
import { Open20Regular } from "@fluentui/react-icons";
import "./WelcomePage.css";

interface WelcomePageProps {
  actionLabel: string;
  onAction: () => void;
  loading?: boolean;
  error?: string | null;
  children?: React.ReactNode;
}

const useStyles = makeStyles({
  icon: { verticalAlign: "text-bottom" },
  link: {
    ...typographyStyles.body1,
    ":hover": { cursor: "pointer" },
  }
});

export const WelcomePage: React.FC<WelcomePageProps> = ({
  actionLabel,
  onAction,
  loading = false,
  error = null,
  children,
}) => {
  const styles = useStyles();
  return (
    <div className="welcome-page">
      <div className="welcome-page__hero">
        <img
          src="/assets/Styling Assistant App Graphic.svg"
          alt="Styling Assistant"
          className="welcome-page__logo"
        />
      </div>
      <div className="welcome-page__content">
        <h2 className="welcome-page__heading">Welcome to Styling Assistant</h2>
        {children ?? (
          <>
            <p className="welcome-page__description">
              Styling Assistant can quickly and easily suggest styling for a manuscript
              and will help you review and accept suggestions.
            </p>
            <h3 className="welcome-page__subheading">Styling Assistant is fully integrated with Word</h3>
            <p className="welcome-page__description">
              You may review and assign styles normally in addition to using Styling Assistant.
            </p>
            <h3 className="welcome-page__subheading">Leveraging AI</h3>
            <p className="welcome-page__description">
              Styling Assistant uses AI alongside PRH style definitions to analyze your document
              and suggest appropriate styling. Styling Assistant will not modify written text.
              Please review suggested styles carefully.
            </p>
            <h3 className="welcome-page__subheading">Continual development</h3>
            <p className="welcome-page__description">
              In its current form, Styling Assistant is limited to assisting production editors
              with styling for simple manuscripts.
            </p>
            <p className="welcome-page__description">
              PRH is actively working to improve the range of manuscripts Styling Assistant
              will be able to help with.
            </p>
          </>
        )}
      </div>
      <div className="welcome-page__action">
        <p className="welcome-page__support-question">Questions about Styling Assistant?</p>
        <Link href="mailto:digitalworkflowsupport@penguinrandomhouse.com" className={styles.link} appearance="subtle" inline>
            Contact support <Open20Regular className={styles.icon}></Open20Regular>
        </Link>
        {loading ? (
          <Spinner label="Signing in…" />
        ) : (
          <Button
            appearance="primary"
            className="welcome-page__button"
            onClick={onAction}
          >
            {actionLabel}
          </Button>
        )}
        {error && (
          <p className="welcome-page__error">{error}</p>
        )}
      </div>
    </div>
  );
};
